import * as React from 'react';
import { AlertMessage, AlertType, CustomSubmitButton, Form, CustomButton, ButtonType, SpinnerWrapper, TextInput } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { TreatmentReportApi } from "../../../api/TreatmentReportApi";
import { ITreatmentReport, ITreatmentReportTreatment, ITreatmentReportEpiReport } from '../../../models/ITreatmentReport';
import { useHistory, useParams } from 'react-router-dom';
import { Treatments } from './Treatments';
import { EpiReport } from "./EpiReport"
import { setMessage } from '../../../helpers/MessageHelper';
import { dateTimeToDateString } from '../../../helpers/DateHelper';
import { formatPersonalSecurityNumberAndOrganisationNumber } from "../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper";
import { TreatmentReportEventLog } from './TreatmentReportEventLog';

export const TreatmentReport: React.FunctionComponent<{}> = () => {
    const auth = useAuth();
    const api = new TreatmentReportApi(auth);
    const [treatmentReport, setTreatmentReport] = React.useState<ITreatmentReport>();
    const { id } = useParams<{ id: string }>();
    const [errorMessages, setErrorMessages] = React.useState<string[]>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const history = useHistory();
    const [isApiCallPending, setIsApiCallPending] = React.useState<boolean>(false);
    const [isLoadingTreatmentReport, setIsLoadingTreatmentReport] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (id) {
            getTreatmentReport(id);
        }
    }, [])

    const getTreatmentReport = (id: string) => {
        setIsLoadingTreatmentReport(true);
        api.getTreatmentReport(id, (report: ITreatmentReport) => {
            setTreatmentReport(report);
            if (report.isValid) {
                setErrorMessages(undefined);
            }
            else {
                setErrorMessages(report.notValidReasons);
            }
        }, undefined, () => setIsLoadingTreatmentReport(false));
    }

    const onSubmit = (treatment: ITreatmentReportTreatment, onCallEnd?: () => void) => {
        if (treatmentReport) {
            treatment.reportId = treatmentReport?.id;

            api.addTreatment(treatment, () => {
                getTreatmentReport(treatment.reportId.toString());
                const newMessage = setMessage("Åtgärdskoden har lagts till", successMessage ?? "");
                setSuccessMessage(newMessage);
                document.getElementById("close-treatment-modal")?.click();
                if (onCallEnd) {
                    onCallEnd();
                }
            }, (message) => {
                const newMessage = setMessage(message, errorMessage ?? "");
                setErrorMessage(newMessage);
                if (onCallEnd) {
                    onCallEnd();
                }
            });
        }
    }

    const onCloseTreatmentReport = () => {
        if (id) {
            setIsApiCallPending(true);
            api.closeTreatmentReport(id, () => {
                getTreatmentReport(id);
                setIsApiCallPending(false);
            });
        }
    }

    const onDelete = (patientTreatmentId: number) => {
        api.deleteTreatment(patientTreatmentId, () => {
            if (id) {
                getTreatmentReport(id);
                const newMessage = setMessage("Åtgärdskoden har tagits bort", successMessage ?? "");
                setSuccessMessage(newMessage);
            }
        });
    }

    const onEpiReportSubmit = (epiReport: ITreatmentReportEpiReport, onCallEnd?: () => void) => {
        if (treatmentReport) {
            epiReport.reportId = treatmentReport.id;

            if (epiReport.epiReportId == -1) {
                api.createEpiReport(epiReport, () => {
                    getTreatmentReport(epiReport.reportId.toString());
                    const newMessage = setMessage("EPI-rapporten har skapats", successMessage ?? "");
                    setSuccessMessage(newMessage);
                    document.getElementById("close-epi-report-modal")?.click();
                    if (onCallEnd) {
                        onCallEnd();
                    }
                }, (message) => {
                    const newMessage = setMessage(message, errorMessage ?? "");
                    setErrorMessage(newMessage);
                    if (onCallEnd) {
                        onCallEnd();
                    }
                });
            }
        }
    }

    const onDeleteEpiReport = (epiReportId: number) => {
        api.deleteEpiReport(epiReportId, () => {
            if (id) {
                getTreatmentReport(id);
                const newMessage = setMessage("EPI-rapporten har tagits bort", successMessage ?? "");
                setSuccessMessage(newMessage);
            }
        });
    }

    const renderTreatmentReportSuccessStatus = () => {
        if (treatmentReport?.revisionEndDate) {
            return `Vårdrapporten fastställdes ${dateTimeToDateString(treatmentReport.revisionEndDate)}. Datum för revisionsslut ${dateTimeToDateString(treatmentReport.treatmentPeriodRevisionEndDate)}.`;
        }
        else if (treatmentReport?.isValid) {
            return "Vårdrapporten är fullständig";
        }
        return undefined;
    }

    return (
        <SpinnerWrapper isLoading={treatmentReport === undefined}>
            <section className="card">
                <div className="card-header">
                    <h2>Hantera vårdrapport</h2>
                </div>
                <div className="card-body">
                    <AlertMessage alertType={AlertType.success} alwaysShow renderOnTopOfPage={false}>
                        {
                            renderTreatmentReportSuccessStatus()
                        }
                    </AlertMessage>
                    <AlertMessage alertType={AlertType.danger} alwaysShow renderOnTopOfPage={false}>
                        {
                            errorMessages && errorMessages.length > 0 && treatmentReport?.revisionEndDate === null ?
                                <>
                                    <p>Följande krav måste uppfyllas för att vårdrapporten ska bli fullständig:</p>
                                    <ul style={{ marginBottom: 0 }}>
                                        {
                                            errorMessages.map((message, key) =>
                                                <li key={key}>{message}</li>
                                            )
                                        }
                                    </ul>
                                </>
                                : undefined
                        }
                    </AlertMessage>
                    <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                    <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                    {
                        treatmentReport ?
                            <div>
                                <SpinnerWrapper isLoading={isLoadingTreatmentReport}>
                                    <div className="row">
                                        <div className="col-6">
                                            <TextInput name="careProviderName" label="Vårdgivare" defaultValue={treatmentReport?.careProvider?.displayOrganisationInformation} readonly={true} inlineLabel={true} />
                                            <TextInput name="careProviderClinicNbr" label="Mottagning" defaultValue={treatmentReport?.careProvider?.displayCareProviderName} readonly={true} inlineLabel={true} />                                           
                                        </div>
                                        <div className="col-6">
                                            <TextInput name="createdDate" label="Skapad" defaultValue={dateTimeToDateString(treatmentReport?.createdDate) ?? "Datum saknas"} readonly={true} inlineLabel={true} />
                                        </div>
                                    </div>
                                    <hr />
                                    <EpiReport treatmentReport={treatmentReport} onSubmit={onEpiReportSubmit} onDelete={onDeleteEpiReport} />
                                    <hr />
                                    <Treatments patientId={treatmentReport.patientId} onSubmit={onSubmit} registeredTreatments={treatmentReport?.treatments} onDelete={onDelete} isEditable={treatmentReport?.isEditable} />
                                    <hr />
                                    <TreatmentReportEventLog id="emergencyTreatmentReportEventLog" treatmentReportEvents={treatmentReport.treatmentReportEvents} />
                                </SpinnerWrapper>
                            </div>
                            : null
                    }
                </div>
                <div className="card-footer">
                    <div className="row">
                        <div className="col-6 text-left">
                            <CustomButton buttonType={ButtonType.general} onClick={() => history.goBack()} buttonText="Gå tillbaka" />
                        </div>
                        {
                            treatmentReport && treatmentReport.isEditable ?
                                <div className="col-6 text-right">
                                    <Form onSubmit={onCloseTreatmentReport}>
                                        <CustomSubmitButton
                                            disabled={!treatmentReport?.isValid}
                                            title={!treatmentReport?.isValid ? "Vårdrapporten är ej fullständig" : ""}
                                            isLoading={isApiCallPending}
                                        >
                                            Skapa revisionsperiod
                                        </CustomSubmitButton>
                                    </Form>
                                </div>
                                : null
                        }
                    </div>
                </div>
            </section>
        </SpinnerWrapper>
    )
}